import { useCallback, useEffect, useMemo, useState } from "react";
import { reportsService, groupService } from "@/services";
import type { KametiGroup, PayoutView } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { PrintLayout } from "@/components/feature/PrintLayout";
import { DateRangePicker, type DateRange } from "@/components/base/DateRangePicker";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINR } from "@/utils/format";
import { paymentModeLabel } from "@/pages/collection/components/labels";
import { payoutStatusMeta } from "@/pages/payout/components/labels";
import { exportReportCSV, csvAmount } from "@/utils/report";

const STATUS_OPTIONS = [
  { value: "pending_approval", label: "Awaiting approval" },
  { value: "approved", label: "Approved" },
  { value: "partially_disbursed", label: "Partial" },
  { value: "disbursed", label: "Disbursed" },
  { value: "reversed", label: "Reversed" },
];

export default function PayoutRegisterPage() {
  const [range, setRange] = useState<DateRange>(() => {
    const now = new Date();
    return { start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), end: now.toISOString() };
  });
  const [groupId, setGroupId] = useState("");
  const [status, setStatus] = useState("");
  const [mode, setMode] = useState("");
  const [data, setData] = useState<{ rows: PayoutView[]; totals: { prize: number; dues: number; netPayable: number; netPaid: number } } | null>(null);
  const [groups, setGroups] = useState<KametiGroup[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [d, g] = await Promise.all([
        reportsService.getPayoutRegister({ range, groupId, status, mode }),
        groupService.listGroups(),
      ]);
      setData(d);
      setGroups(g);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the payout register.");
      setData(null);
    }
  }, [range, groupId, status, mode]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilters = (groupId ? 1 : 0) + (status ? 1 : 0) + (mode ? 1 : 0) + (range.start ? 1 : 0);
  const clearFilters = () => {
    setGroupId("");
    setStatus("");
    setMode("");
    setRange({ start: null, end: null });
  };

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (range.start) parts.push(`From ${formatDate(range.start)}`);
    if (range.end) parts.push(`To ${formatDate(range.end)}`);
    if (groupId) parts.push(groups.find((g) => g.id === groupId)?.name ?? "Group");
    if (status) parts.push(status);
    if (mode) parts.push(mode);
    return parts.length ? parts.join(" · ") : "All payouts";
  }, [range, groupId, status, mode, groups]);

  const handleExport = () => {
    if (!data) return;
    exportReportCSV({
      filename: "payout-register",
      reportName: "Payout Register",
      filters: [filterSummary],
      columns: [
        { header: "Voucher", value: (r) => r.voucher },
        { header: "Date", value: (r) => r.date },
        { header: "Group", value: (r) => r.group },
        { header: "Cycle", value: (r) => r.cycle },
        { header: "Winner", value: (r) => r.winner },
        { header: "Prize", value: (r) => csvAmount(r.prize) },
        { header: "Dues adj", value: (r) => csvAmount(r.dues) },
        { header: "Net payable", value: (r) => csvAmount(r.net) },
        { header: "Net paid", value: (r) => csvAmount(r.paid) },
        { header: "Mode", value: (r) => r.mode },
        { header: "Status", value: (r) => r.status },
      ],
      rows: data.rows.map((r) => ({
        voucher: r.voucherNumber,
        date: formatDate(r.createdAt),
        group: `${r.groupName} (${r.groupCode})`,
        cycle: r.cycle,
        winner: r.winnerName,
        prize: r.prize,
        dues: r.sameGroupDuesTotal,
        net: r.netPayable,
        paid: r.amountPaid,
        mode: r.disbursements[r.disbursements.length - 1]?.mode ?? "",
        status: payoutStatusMeta(r.status).label,
      })),
    });
  };

  return (
    <div>
      <PageHeader
        className="no-print"
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Payout register" isLast />
          </>
        }
        title="Payout register"
        subtitle="All payouts across groups with a totals row."
      />

      <div className="no-print mb-3 flex flex-wrap items-center gap-2">
        <DateRangePicker value={range} onChange={setRange} />
        <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">All groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">All modes</option>
          {["cash", "upi", "bank_transfer", "cheque"].map((m) => (
            <option key={m} value={m}>{paymentModeLabel(m as PayoutView["disbursements"][number]["mode"])}</option>
          ))}
        </select>
        {activeFilters > 0 ? (
          <button type="button" onClick={clearFilters} className="cursor-pointer text-xs font-medium text-primary-600 hover:underline">
            Clear filters ({activeFilters})
          </button>
        ) : null}
      </div>

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!data ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : data.rows.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <EmptyState icon="ri-bank-line" title="No payouts match" description="No payouts were recorded in this range with the current filters." />
        </div>
      ) : (
        <PrintLayout
          title="Payout Register"
          meta={filterSummary}
          actions={<Button size="sm" variant="outline" icon={<i className="ri-download-2-line" />} onClick={handleExport}>Export</Button>}
        >
          <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-background-200 bg-background-200 sm:grid-cols-4">
            <TotalCell label="Total prize" value={formatINR(data.totals.prize)} />
            <TotalCell label="Dues adjusted" value={formatINR(data.totals.dues)} />
            <TotalCell label="Net payable" value={formatINR(data.totals.netPayable)} />
            <TotalCell label="Net paid" value={formatINR(data.totals.netPaid)} accent />
          </div>

          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-background-100 text-foreground-600">
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Voucher</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Group</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Cycle</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Winner</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Prize</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Dues adj</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Net payable</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Net paid</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Mode</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((p) => (
                <tr key={p.id} className="border-t border-background-100">
                  <td className="tnum whitespace-nowrap px-3 py-2 font-mono font-semibold text-primary-700">{p.voucherNumber}</td>
                  <td className="tnum whitespace-nowrap px-3 py-2">{formatDate(p.createdAt)}</td>
                  <td className="px-3 py-2 font-medium text-foreground-900">{p.groupName}<span className="tnum ml-1 text-foreground-500 font-normal">({p.groupCode})</span></td>
                  <td className="tnum px-3 py-2 font-medium">{p.cycle}</td>
                  <td className="px-3 py-2 font-medium text-foreground-900">{p.winnerName}</td>
                  <td className="px-3 py-2 text-right"><CurrencyDisplay amount={p.prize} hideSymbol /></td>
                  <td className="px-3 py-2 text-right text-danger-600"><CurrencyDisplay amount={p.sameGroupDuesTotal} hideSymbol /></td>
                  <td className="px-3 py-2 text-right font-semibold"><CurrencyDisplay amount={p.netPayable} hideSymbol /></td>
                  <td className="px-3 py-2 text-right font-semibold"><CurrencyDisplay amount={p.amountPaid} hideSymbol tone={p.status === "reversed" ? "danger" : "default"} /></td>
                  <td className="px-3 py-2 capitalize">{p.disbursements[p.disbursements.length - 1] ? paymentModeLabel(p.disbursements[p.disbursements.length - 1].mode) : "—"}</td>
                  <td className="px-3 py-2"><Badge color={payoutStatusMeta(p.status).color}>{payoutStatusMeta(p.status).label}</Badge></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-background-200 bg-background-100/60 font-semibold">
                <td className="px-3 py-2.5 text-foreground-600 uppercase text-xs tracking-wider" colSpan={5}>Total</td>
                <td className="px-3 py-2.5 text-right font-bold text-base"><CurrencyDisplay amount={data.totals.prize} hideSymbol /></td>
                <td className="px-3 py-2.5 text-right text-danger-600 font-bold text-base"><CurrencyDisplay amount={data.totals.dues} hideSymbol /></td>
                <td className="px-3 py-2.5 text-right font-bold text-base"><CurrencyDisplay amount={data.totals.netPayable} hideSymbol /></td>
                <td className="px-3 py-2.5 text-right font-bold text-base"><CurrencyDisplay amount={data.totals.netPaid} hideSymbol /></td>
                <td className="px-3 py-2.5" colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </PrintLayout>
      )}
    </div>
  );
}

function TotalCell({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`p-3.5 ${accent ? "bg-background-100" : "bg-background-50"}`}>
      <p className="text-xs uppercase tracking-wider text-foreground-500 font-medium">{label}</p>
      <p className={`tnum mt-1 text-base font-bold ${accent ? "text-primary-700" : "text-foreground-950"}`}>{value}</p>
    </div>
  );
}